/**
 * Shared TanStack Query hooks for the Workloads page.
 *
 * The tree at the top and every detail pane below it consume these so
 * fetched data is shared across siblings (no duplicate fetches).
 */
import { useQuery } from "@tanstack/react-query";

import { api } from "@/api/client";
import type { Pool, Replica, Service } from "@/api/types";

export function useServices() {
  return useQuery({
    queryKey: ["services"],
    queryFn: () => api.get<Service[]>("/services"),
    staleTime: 10_000,
  });
}

export function useAllReplicas() {
  return useQuery({
    queryKey: ["replicas", "all"],
    queryFn: () => api.get<Replica[]>("/replicas"),
    refetchInterval: 8_000,
  });
}

export function useAllPools(serviceIds: number[] | undefined) {
  return useQuery({
    queryKey: ["pools", "all", (serviceIds ?? []).slice().sort().join(",")],
    enabled: serviceIds !== undefined && serviceIds.length > 0,
    queryFn: async () => {
      const lists = await Promise.all(
        (serviceIds ?? []).map((sid) =>
          api.get<Pool[]>(`/services/${sid}/pools`),
        ),
      );
      return lists.flat();
    },
    staleTime: 15_000,
  });
}
